import {
  Avatar,
  Box,
  Button,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconFlag, IconPrinter } from "@tabler/icons-react";
import { useRef } from "react";
import ReactToPrint from "react-to-print";
import { Board } from "../../common/boards/board.schema";
import { Event, Pool } from "../../common/events/event.schema";

import { Member } from "../../common/members/member.schema";
import RecordAvatar from "../common/RecordAvatar";
import MemberDisplay from "../members/MemberDisplay";
import MemberName from "../members/MemberName";
import PrintFooter from "../print/PrintFooter";
import { getMatchups, PoolMatchup } from "./NextMatches";
import { getDisplayRows, PoolTableRow } from "./PoolTable";
import classes from "./PrintPoolTable.module.css";
const PrintPoolTable = ({
  disabled,
  event,
  board,
  pool,
  debug,
}: {
  disabled?: boolean;
  debug?: boolean;
  event: Event;
  pool: Pool;
  board: Board;
}) => {
  const componentRef = useRef<any>();
  const members = event.members as Member[];
  //   const image = event.image || board.image;
  const activeMembers = pool.members.filter((pm) => !pm.withdrawn);
  // console.log("activeMembers :", activeMembers);
  const withdrawals = pool.members.filter((pm) => pm.withdrawn);
  let displayRows: (PoolTableRow | null)[] = getDisplayRows(pool, event);
  const matchups: PoolMatchup[] = getMatchups(event, pool, true);

  const matchupCols: PoolMatchup[][] = [];
  let column: PoolMatchup[] = [];
  const maxRows = 12;
  for (let i = 0; i < matchups.length; i++) {
    if (i % maxRows === 0 && i !== 0) {
      matchupCols.push(column);
      column = [];
    }
    column.push(matchups[i]);
  }
  matchupCols.push(column);
  // console.log("displayRows :", displayRows);
  displayRows = displayRows.filter((r) => !!r);
  const image = event.image || board.image;
  // return <></>;
  return (
    <>
      <ReactToPrint
        trigger={() => (
          <Button
            leftSection={<IconPrinter size={15} />}
            variant="subtle"
            disabled={disabled}
            size="xs"
          >
            Print pool sheet
          </Button>
        )}
        content={() => componentRef.current || null}
      />
      <div style={{ display: debug ? "block" : "none" }}>
        <Stack align="center" ref={componentRef} py={20} px={20}>
          <Stack align="center">
            <Group>
              {image && <Avatar src={image} />}
              <Title ta="center">{board.name}</Title>
            </Group>
            <Title order={2} ta="center">
              {event.name}
            </Title>
            <Title order={3} ta="center">
              Pool {event.pools.indexOf(pool) + 1}
            </Title>
          </Stack>
          <Box w="100%">
            <Table
              borderColor="black"
              className="printTable"
              withColumnBorders
              withTableBorder
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th></Table.Th>
                  <Table.Th className={classes.indexCell}></Table.Th>
                  {activeMembers.map((pm, i) => {
                    return (
                      <Table.Th
                        key={i}
                        ta="center"
                        className={classes.centerHeadIndex}
                      >
                        {i + 1}
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {displayRows.map((row) => {
                  if (!row) return;
                  // console.log('member', member._id)
                  const { member, index } = row;
                  return (
                    <Table.Tr key={member._id}>
                      <Table.Td className={classes.nameCell}>
                        <Group>
                          <RecordAvatar record={member} size="sm" />
                          <MemberName member={member} />
                        </Group>
                      </Table.Td>
                      <Table.Td width={50} className={classes.indexCell}>
                        {index + 1}
                      </Table.Td>
                      {row.opponents.map((opponent) => {
                        const { self, match, points, winner, index } = opponent;
                        const key = `${member._id}-${index}`;
                        // console.log('opponent', opponent.member)
                        if (self)
                          return (
                            <Table.Td
                              width={50}
                              key={key}
                              className={classes.selfBout}
                            ></Table.Td>
                          );
                        // console.log('match', match)
                        if (match) {
                          if (winner) {
                            return (
                              <Table.Td
                                width={50}
                                key={key}
                                className={classes.scoreCell}
                              >
                                <ThemeIcon color="green" variant="light">
                                  <Text fw={700}>V{points}</Text>
                                </ThemeIcon>
                              </Table.Td>
                            );
                          } else {
                            return (
                              <Table.Td
                                width={50}
                                key={key}
                                className={classes.scoreCell}
                              >
                                <ThemeIcon color="red" variant="light">
                                  <Text fw={700}>D{points}</Text>
                                </ThemeIcon>
                              </Table.Td>
                            );
                          }
                        }
                        return (
                          <Table.Td
                            key={key}
                            width={50}
                            className={classes.scoreCell}
                          ></Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
                {withdrawals.map((row) => {
                  const member = members.find((m) => m._id === row.member);
                  // console.log('member', member._id)
                  if (!member) return null;
                  return (
                    <Table.Tr key={member._id}>
                      <Table.Td className={classes.nameCell}>
                        <Group>
                          <RecordAvatar record={member} size="sm" />
                          <MemberDisplay visibleFrom="md" member={member} />
                          <ThemeIcon size="xs" color="dark" variant="light">
                            <IconFlag />
                          </ThemeIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Box>
          <Box>
            <SimpleGrid cols={matchupCols.length} spacing={40}>
              {matchupCols.map((column, i) => (
                <Box key={i}>
                  <Table borderColor="black">
                    <Table.Tbody>
                      {column.map((matchup) => {
                        const memberA = members.find(
                          (m) => m._id === matchup.a.member
                        );
                        const memberB = members.find(
                          (m) => m._id === matchup.b.member
                        );
                        const match = matchup.match;
                        if (!memberA || !memberB) return null;
                        const aWinner = match?.winner === memberA._id;
                        return (
                          <Table.Tr key={matchup.a.member + matchup.b.member}>
                            <Table.Td align="right">
                              <Group wrap="nowrap" align="right">
                                {match && (
                                  <ThemeIcon
                                    color={aWinner ? "green" : "red"}
                                    variant="light"
                                  >
                                    <Text size="xs" fw={700}>
                                      {aWinner
                                        ? `V${match.winnerPoints}`
                                        : `D${match.loserPoints}`}
                                    </Text>
                                  </ThemeIcon>
                                )}
                                <Text size="xs">
                                  <MemberName member={memberA} max={20} />
                                </Text>
                              </Group>
                            </Table.Td>
                            <Table.Td width={40} align="center">
                              <ThemeIcon size="xs" variant="light">
                                <Text fz="xs">vs</Text>
                              </ThemeIcon>
                            </Table.Td>
                            <Table.Td>
                              <Group wrap="nowrap">
                                <Text size="xs">
                                  <MemberName member={memberB} max={20} />
                                </Text>
                                {match && (
                                  <ThemeIcon
                                    color={!aWinner ? "green" : "red"}
                                    variant="light"
                                  >
                                    <Text size="xs" fw={700}>
                                      {!aWinner
                                        ? `V${match.winnerPoints}`
                                        : `D${match.loserPoints}`}
                                    </Text>
                                  </ThemeIcon>
                                )}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
          <PrintFooter />
        </Stack>
      </div>
    </>
  );
};
export default PrintPoolTable;
